import {
  CSSProperties,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef
} from 'react';
import { List, RowComponentProps, useListRef } from 'react-window';
import { ActivityKind } from '@blisspointmedia/bpm-types/dist/OracleActivity';
import { Spinner } from '@Components/Spinner/Spinner';
import { ActivityId } from '../activity-feed-data-utils';
import { useActivityFeedData } from '../useActivityFeedData';
import { useMultiSelect } from '../useActivityFeedMultiSelect';
import { useSelection } from '../useActivityFeedSelection';
import { useToggleActivityState } from '../useOracleEventMutations';
import { EventCard, EventCardToggleHandler } from './EventCard';
import { FeedGroupDivider } from './FeedGroupDivider';
import { FeedListHeader } from './FeedListHeader';
import {
  rowHeightFor,
  shouldPrefetchOnScroll,
  TAIL_PREFETCH_ROWS,
  VirtualRow
} from './activity-feed-list-utils';

interface RowProps {
  rows: VirtualRow[];
  selectedId: ActivityId | null;
  onSelectId: (id: ActivityId) => void;
  checkedIds: ReadonlySet<ActivityId>;
  onToggleCheckedId: (id: ActivityId) => void;
  onToggleState: EventCardToggleHandler;
  activitiesById: ReturnType<typeof useActivityFeedData>['activitiesById'];
  isFetchingNextPage: boolean;
}

export const ActivityFeedList = (): ReactNode => (
  <div className='activity-feed-list'>
    <FeedListHeader />
    <ActivityFeedListBody />
  </div>
);

const ActivityFeedListBody = (): ReactNode => {
  const { groups, activitiesById, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useActivityFeedData();
  const { selectedId, setSelectedId } = useSelection();
  const { checkedIds, toggleCheckedId } = useMultiSelect();

  // Stable id-taking handler so memo(EventCard) sees the same prop across parent re-renders
  // that don't touch the card. (Handing down a per-card closure would defeat the memo.)
  const onSelectId = useCallback((id: ActivityId) => setSelectedId(id), [setSelectedId]);

  // Hoisted here so cards receive a stable handler prop instead of each subscribing to
  // ActivityFeedDataContext via useToggleActivityState — the latter would defeat memo(EventCard)
  // for every card on every state change (optimistic mutation, filter, search).
  const onToggleState = useToggleActivityState();

  const rows = useMemo<VirtualRow[]>(() => {
    const out: VirtualRow[] = [];
    for (const group of groups) {
      out.push({ kind: 'divider', key: `divider-${group.key}`, label: group.label });
      for (const id of group.items) {
        out.push({ kind: 'card', key: id, activityId: id });
      }
    }
    if (hasNextPage) {
      out.push({ kind: 'footer', key: 'footer' });
    }
    return out;
  }, [groups, hasNextPage]);

  // Keyed on selectedId alone, so paging and filtering never pull the list back to the selection.
  const listRef = useListRef(null);
  const rowsRef = useRef(rows);
  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);
  useEffect(() => {
    const index = rowsRef.current.findIndex(
      row => row.kind === 'card' && row.activityId === selectedId
    );
    if (index >= 0) {
      listRef.current?.scrollToRow({ index, align: 'auto', behavior: 'smooth' });
    }
  }, [selectedId, listRef]);

  // Keep paging until either the visible list covers the prefetch window or the server has
  // no more pages. Filtering is client-side, so a narrow filter can leave the loaded set with
  // fewer visible cards than the prefetch window even though more matches exist upstream —
  // the small-N threshold keeps the fetch loop going until we have enough or hasNextPage flips.
  const cardCount = rows.filter(row => row.kind === 'card').length;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && cardCount < TAIL_PREFETCH_ROWS) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, cardCount, fetchNextPage]);

  const onRowsRendered = useCallback(
    ({ stopIndex }: { startIndex: number; stopIndex: number }) => {
      if (
        shouldPrefetchOnScroll({
          rowCount: rows.length,
          stopIndex,
          hasNextPage,
          isFetchingNextPage
        })
      ) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, rows.length, fetchNextPage]
  );

  if (!isFetchingNextPage && rows.length === 0) {
    return (
      <div className='activity-feed-list-body activity-feed-list-empty'>No events to display.</div>
    );
  }

  const rowProps: RowProps = {
    rows,
    selectedId,
    onSelectId,
    checkedIds,
    onToggleCheckedId: toggleCheckedId,
    onToggleState,
    activitiesById,
    isFetchingNextPage
  };

  return (
    <div className='activity-feed-list-body'>
      <List
        className='activity-feed-vlist'
        listRef={listRef}
        defaultHeight={800}
        rowCount={rows.length}
        rowHeight={(index, props) => rowHeightFor(props.rows[index])}
        rowComponent={ActivityFeedRow}
        rowProps={rowProps}
        rowKey={(index, props) => props.rows[index].key}
        onRowsRendered={onRowsRendered}
      />
    </div>
  );
};

const ActivityFeedRow = ({
  index,
  style,
  rows,
  selectedId,
  onSelectId,
  checkedIds,
  onToggleCheckedId,
  onToggleState,
  activitiesById,
  isFetchingNextPage
}: RowComponentProps<RowProps>): ReactElement | null => {
  const row = rows[index];
  if (row.kind === 'divider') {
    return (
      <div style={style}>
        <FeedGroupDivider label={row.label} />
      </div>
    );
  }
  if (row.kind === 'footer') {
    return <FooterRow style={style} isFetching={isFetchingNextPage} />;
  }
  return (
    <CardRow
      style={style}
      row={row}
      selectedId={selectedId}
      onSelectId={onSelectId}
      checkedIds={checkedIds}
      onToggleCheckedId={onToggleCheckedId}
      onToggleState={onToggleState}
      activitiesById={activitiesById}
    />
  );
};

interface CardRowProps {
  style: CSSProperties;
  row: Extract<VirtualRow, { kind: 'card' }>;
  selectedId: ActivityId | null;
  onSelectId: (id: ActivityId) => void;
  checkedIds: ReadonlySet<ActivityId>;
  onToggleCheckedId: (id: ActivityId) => void;
  onToggleState: EventCardToggleHandler;
  activitiesById: ReturnType<typeof useActivityFeedData>['activitiesById'];
}

const CardRow = ({
  style,
  row,
  activitiesById,
  selectedId,
  onSelectId,
  checkedIds,
  onToggleCheckedId,
  onToggleState
}: CardRowProps): ReactNode => {
  const activity = activitiesById[row.activityId];
  if (!activity) return <div style={style} />;
  if (activity.kind !== ActivityKind.EVENT) return <div style={style} />;
  return (
    <div style={style}>
      <EventCard
        event={activity}
        isSelected={row.activityId === selectedId}
        isChecked={checkedIds.has(row.activityId)}
        onToggleCheckedId={onToggleCheckedId}
        onSelectId={onSelectId}
        onToggleState={onToggleState}
      />
    </div>
  );
};

interface FooterRowProps {
  style: CSSProperties;
  isFetching: boolean;
}

const FooterRow = ({ style, isFetching }: FooterRowProps): ReactNode => (
  <div className='activity-feed-list-footer' style={style}>
    {isFetching ? <Spinner /> : null}
  </div>
);
